import React, { useCallback, useEffect, useState } from 'react';
import StaffAnswersPanel, { emptyAnswerDraft, suggestAnswerId, type StaffAnswerDraft } from '../components/support/StaffAnswersPanel';

/**
 * STAFF Support Desk (tracker #51; staff-desk MINIMUM, owner ruling
 * 2026-07-10): the Edinshall-side queue over escalated/direct tickets so
 * support UAT can run end-to-end. Item #16 adds the governed minimum time
 * capture here; roster, claim-lease, SLA clocks, canned responses and
 * internal notes remain with the full slice-5 desk.
 */

interface StaffTicketSummary {
  ticketId: string;
  tenantId: string;
  instanceId: string | null;
  subject: string;
  origin: string;
  category: string | null;
  status: string;
  escalatedBy: string | null;
  planId: string | null;
  slaStartAtUtc: string;
  createdAtUtc: string;
  updatedAtUtc: string;
  closedAtUtc: string | null;
}

interface StaffAttachment {
  attachmentId: string;
  fileName: string;
  contentType: string | null;
  sizeBytes: number;
  quarantineStatus: string;
}

interface StaffTicketMessage {
  messageId: string;
  authorType: string;
  authorName: string | null;
  authorUpn: string | null;
  body: string;
  fromTierZero: boolean;
  createdAtUtc: string;
  attachments: StaffAttachment[];
}

interface StaffTicketDetail extends StaffTicketSummary {
  diagnosticBundleJson: string | null;
  messages: StaffTicketMessage[];
  ticketAttachments?: StaffAttachment[];
  timeEntries: Array<{
    timeEntryId: string;
    staffUpn: string | null;
    quarterHours: number;
    hours: number;
    note: string | null;
    status: 'pending' | 'confirmed';
    createdAtUtc: string;
    confirmedAtUtc: string | null;
  }>;
}

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  triaged: 'Triaged',
  waiting_on_client: 'Waiting on client',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed'
};

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

/** Tracker #106 D16: the plan a ticket carries a commitment under (null on pre-V100 rows). */
const PLAN_LABELS: Record<string, string> = { free: 'Free', standard: 'Standard', enterprise: 'Enterprise' };
const PLAN_FILTERS = ['all', 'free', 'standard', 'enterprise', 'unknown'] as const;
type PlanFilter = (typeof PLAN_FILTERS)[number];
const planLabel = (planId: string | null): string => (planId ? PLAN_LABELS[planId] ?? planId : 'Plan unknown');
const planPillClass = (planId: string | null): string =>
  planId === 'enterprise' ? 'pill success' : planId === 'standard' ? 'pill' : 'badge-subtle';
const matchesPlanFilter = (planId: string | null, filter: PlanFilter): boolean =>
  filter === 'all' ? true : filter === 'unknown' ? !planId : planId === filter;

const statusPillClass = (status: string): string => {
  if (status === 'new') return 'pill warning';
  if (status === 'resolved' || status === 'closed') return 'badge-subtle';
  return 'pill success';
};

const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as { message?: string };
  return body.message ?? fallback;
};

const SupportDesk = (): JSX.Element => {
  const [tickets, setTickets] = useState<StaffTicketSummary[]>([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const [view, setView] = useState<'queue' | 'answers'>('queue');
  const [promotedDraft, setPromotedDraft] = useState<StaffAnswerDraft | undefined>();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<StaffTicketDetail>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [showBundle, setShowBundle] = useState(false);
  const [reply, setReply] = useState('');
  const [timeHours, setTimeHours] = useState('0.25');
  const [timeNote, setTimeNote] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const loadTickets = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetch(`/api/admin/support/tickets?includeClosed=${includeClosed}`);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      const body = (await response.json()) as { tickets?: StaffTicketSummary[] };
      setTickets(Array.isArray(body.tickets) ? body.tickets : []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [includeClosed]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  const visibleTickets = tickets.filter((ticket) => matchesPlanFilter(ticket.planId, planFilter));

  const openTicket = useCallback(async (ticketId: string): Promise<void> => {
    setDetailLoading(true);
    setError(undefined);
    setNotice(undefined);
    setReply('');
    setTimeHours('0.25');
    setTimeNote('');
    setShowBundle(false);
    try {
      const response = await fetch(`/api/admin/support/tickets/${encodeURIComponent(ticketId)}`);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      const body = (await response.json()) as { ticket?: StaffTicketDetail };
      setDetail(body.ticket);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const sendReply = async (): Promise<void> => {
    if (!detail || !reply.trim()) {
      return;
    }
    setWorking(true);
    setError(undefined);
    try {
      const response = await fetch(`/api/admin/support/tickets/${encodeURIComponent(detail.ticketId)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: reply.trim() })
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      setReply('');
      await openTicket(detail.ticketId);
      await loadTickets();
      setNotice('Reply sent; ticket is now waiting on the client.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const setStatus = async (status: string): Promise<void> => {
    if (!detail) {
      return;
    }
    setWorking(true);
    setError(undefined);
    try {
      const response = await fetch(`/api/admin/support/tickets/${encodeURIComponent(detail.ticketId)}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      await openTicket(detail.ticketId);
      await loadTickets();
      setNotice(`Ticket status set to ${statusLabel(status)}.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const addTime = async (): Promise<void> => {
    if (!detail) return;
    const hours = Number(timeHours);
    const quarterHours = Math.round(hours * 4);
    if (!Number.isFinite(hours) || quarterHours < 1 || quarterHours > 96 || Math.abs(hours * 4 - quarterHours) > 0.0001) {
      setError('Enter between 0.25 and 24 hours in quarter-hour increments.');
      return;
    }
    setWorking(true);
    setError(undefined);
    try {
      const response = await fetch(`/api/admin/support/tickets/${encodeURIComponent(detail.ticketId)}/time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quarterHours, note: timeNote.trim() })
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      setTimeHours('0.25');
      setTimeNote('');
      await openTicket(detail.ticketId);
      setNotice('Support time added as pending. It becomes metered usage only when the ticket is resolved or closed.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const removeTime = async (timeEntryId: string): Promise<void> => {
    if (!detail) return;
    setWorking(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/api/admin/support/tickets/${encodeURIComponent(detail.ticketId)}/time/${encodeURIComponent(timeEntryId)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      await openTicket(detail.ticketId);
      setNotice('Pending support time removed.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <h1 className="section-title">Support Desk</h1>
      <p className="muted">
        Escalated and directly raised tickets from every customer tenant, oldest SLA first. Replying
        hands the ticket back to the client. Staff time is entered in quarter-hours and confirmed
        for metering only on resolve or close. Roster, claim, SLA clocks and canned responses arrive
        with the full desk.
      </p>
      {error && <div className="pill warning">{error}</div>}
      {notice && <div className="pill success">{notice}</div>}

      {view === 'answers' && !detail && (
        <StaffAnswersPanel
          initialDraft={promotedDraft}
          onBack={() => { setPromotedDraft(undefined); setView('queue'); }}
        />
      )}

      {view === 'queue' && !detail && (
        <div className="card" style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <h3 style={{ margin: 0 }}>Queue</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button type="button" className="btn secondary" onClick={() => setView('answers')} disabled={loading}>
                Answers
              </button>
              <label className="muted" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                Plan
                <select
                  aria-label="Filter queue by plan"
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value as PlanFilter)}
                >
                  {PLAN_FILTERS.map((filter) => (
                    <option key={filter} value={filter}>
                      {filter === 'all' ? 'All plans' : filter === 'unknown' ? 'Plan unknown' : PLAN_LABELS[filter]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="muted" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={includeClosed}
                  onChange={(e) => setIncludeClosed(e.target.checked)}
                />
                Include resolved/closed
              </label>
              <button type="button" className="btn secondary" onClick={() => void loadTickets()} disabled={loading}>
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
          {loading && <span className="badge-subtle" style={{ marginTop: '10px' }}>Loading queue…</span>}
          {!loading && tickets.length === 0 && (
            <p className="muted" style={{ marginTop: '10px' }}>The queue is empty.</p>
          )}
          {!loading && tickets.length > 0 && visibleTickets.length === 0 && (
            <p className="muted" style={{ marginTop: '10px' }}>No tickets match the plan filter.</p>
          )}
          <div style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
            {visibleTickets.map((ticket) => (
              <div
                key={ticket.ticketId}
                className="list-row"
                role="button"
                tabIndex={0}
                onClick={() => void openTicket(ticket.ticketId)}
                onKeyDown={(e) => e.key === 'Enter' && void openTicket(ticket.ticketId)}
              >
                <div>
                  <strong>{ticket.subject}</strong>
                  <div className="muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                    <span className={planPillClass(ticket.planId)} title="Licence plan at escalation">
                      {planLabel(ticket.planId)}
                    </span>
                    {' · Tenant '}
                    {ticket.tenantId}
                    {ticket.escalatedBy ? ` · ${ticket.escalatedBy}` : ''}
                    {' · '}
                    {ticket.origin === 'escalated' ? 'Escalated' : 'Direct'}
                    {ticket.category ? ` · ${ticket.category}` : ''}
                    {' · SLA from '}
                    {new Date(ticket.slaStartAtUtc).toLocaleString()}
                  </div>
                </div>
                <span className={statusPillClass(ticket.status)}>{statusLabel(ticket.status)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detailLoading && <div className="badge-subtle" style={{ marginTop: '12px' }}>Loading ticket…</div>}

      {detail && !detailLoading && (
        <div className="card" style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <div>
              <h3 style={{ marginBottom: '4px' }}>{detail.subject}</h3>
              <p className="muted" style={{ margin: 0 }}>
                <span className={statusPillClass(detail.status)}>{statusLabel(detail.status)}</span>
                {' · '}
                <span className={planPillClass(detail.planId)}>{planLabel(detail.planId)}</span>
                {' · Tenant '}
                {detail.tenantId}
                {detail.instanceId ? ` · Instance ${detail.instanceId}` : ''}
                {' · Opened '}
                {new Date(detail.createdAtUtc).toLocaleString()}
              </p>
            </div>
            <button type="button" className="btn secondary" onClick={() => setDetail(undefined)}>
              Back to queue
            </button>
          </div>

          {detail.diagnosticBundleJson && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="btn secondary" onClick={() => setShowBundle((v) => !v)}>
                {showBundle ? 'Hide diagnostic bundle' : 'Show diagnostic bundle'}
              </button>
              {showBundle && (
                <pre
                  style={{
                    marginTop: '8px',
                    padding: '10px',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    maxHeight: '260px',
                    overflow: 'auto',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(detail.diagnosticBundleJson ?? ''), undefined, 2);
                    } catch {
                      return detail.diagnosticBundleJson;
                    }
                  })()}
                </pre>
              )}
            </div>
          )}

          {(detail.ticketAttachments ?? []).length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
              {(detail.ticketAttachments ?? []).map((attachment) =>
                attachment.quarantineStatus === 'released' ? (
                  <a
                    key={attachment.attachmentId}
                    className="badge-subtle"
                    href={`/api/admin/support/tickets/${encodeURIComponent(detail.ticketId)}/attachments/${encodeURIComponent(attachment.attachmentId)}/download`}
                  >
                    {attachment.fileName} ({formatBytes(attachment.sizeBytes)})
                  </a>
                ) : (
                  <span key={attachment.attachmentId} className="badge-subtle" title="Held by quarantine — not downloadable.">
                    {attachment.fileName} — {attachment.quarantineStatus}
                  </span>
                )
              )}
            </div>
          )}

          <div className="card" style={{ marginTop: '12px', background: 'var(--panel)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
              <strong>Assisted support time</strong>
              <span className="muted">
                {(detail.timeEntries ?? []).filter((entry) => entry.status === 'confirmed').reduce((sum, entry) => sum + entry.hours, 0)} h confirmed
                {' · '}
                {(detail.timeEntries ?? []).filter((entry) => entry.status === 'pending').reduce((sum, entry) => sum + entry.hours, 0)} h pending
              </span>
            </div>
            {(detail.timeEntries ?? []).length === 0 && (
              <p className="muted" style={{ marginBottom: 0 }}>No staff time has been entered for this ticket.</p>
            )}
            {(detail.timeEntries ?? []).length > 0 && (
              <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                {(detail.timeEntries ?? []).map((entry) => (
                  <div key={entry.timeEntryId} className="list-row">
                    <div>
                      <strong>{entry.hours} h</strong>
                      <span className="muted"> · {entry.status === 'confirmed' ? 'Confirmed usage' : 'Pending until resolve/close'}</span>
                      {entry.note && <div className="muted" style={{ marginTop: '3px' }}>{entry.note}</div>}
                      <div className="muted" style={{ fontSize: '0.8rem', marginTop: '3px' }}>
                        {entry.staffUpn || 'Staff'} · {new Date(entry.createdAtUtc).toLocaleString()}
                      </div>
                    </div>
                    {entry.status === 'pending' && (
                      <button
                        type="button"
                        className="btn secondary"
                        onClick={() => void removeTime(entry.timeEntryId)}
                        disabled={working}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {detail.status !== 'resolved' && detail.status !== 'closed' && (
              <div style={{ display: 'grid', gap: '8px', marginTop: '10px', maxWidth: '640px' }}>
                <label>
                  <span className="label">Hours worked</span>
                  <input
                    className="input"
                    type="number"
                    min="0.25"
                    max="24"
                    step="0.25"
                    value={timeHours}
                    onChange={(event) => setTimeHours(event.target.value)}
                    disabled={working}
                  />
                </label>
                <label>
                  <span className="label">Work note (optional)</span>
                  <input
                    className="input"
                    type="text"
                    maxLength={500}
                    value={timeNote}
                    onChange={(event) => setTimeNote(event.target.value)}
                    placeholder="Briefly describe the assisted work"
                    disabled={working}
                  />
                </label>
                <div>
                  <button type="button" className="btn secondary" onClick={() => void addTime()} disabled={working}>
                    Add pending time
                  </button>
                </div>
                <p className="muted" style={{ margin: 0 }}>
                  Pending entries remain editable and do not enter the usage ledger until this ticket is resolved or closed.
                </p>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gap: '8px', marginTop: '12px' }}>
            {detail.messages.map((message) => {
              const isStaff = message.authorType === 'staff' || message.authorType === 'system';
              return (
                <div
                  key={message.messageId}
                  style={{
                    border: '1px solid var(--border)',
                    borderLeft: isStaff ? '3px solid var(--accent)' : '3px solid var(--border)',
                    borderRadius: '4px',
                    background: 'var(--panel)',
                    padding: '10px 12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                    <strong>
                      {isStaff ? 'Edinshall Support' : (message.authorName || message.authorUpn || 'Client')}
                    </strong>
                    <span className="muted">
                      {message.fromTierZero ? 'From escalated conversation · ' : ''}
                      {new Date(message.createdAtUtc).toLocaleString()}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{message.body}</p>
                  {message.attachments.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {message.attachments.map((attachment) =>
                        attachment.quarantineStatus === 'released' ? (
                          <a
                            key={attachment.attachmentId}
                            className="badge-subtle"
                            href={`/api/admin/support/tickets/${encodeURIComponent(detail.ticketId)}/attachments/${encodeURIComponent(attachment.attachmentId)}/download`}
                          >
                            {attachment.fileName} ({formatBytes(attachment.sizeBytes)})
                          </a>
                        ) : (
                          <span
                            key={attachment.attachmentId}
                            className="badge-subtle"
                            title="Held by quarantine — not downloadable."
                          >
                            {attachment.fileName} — {attachment.quarantineStatus}
                          </span>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {detail.status !== 'closed' && (
            <div style={{ display: 'grid', gap: '8px', marginTop: '12px', maxWidth: '640px' }}>
              <textarea
                className="input"
                rows={4}
                placeholder="Reply as Edinshall Support (hands the ticket back to the client)"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                disabled={working}
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="cta" onClick={() => void sendReply()} disabled={working || !reply.trim()}>
                  {working ? 'Working…' : 'Send reply'}
                </button>
                {detail.status === 'new' && (
                  <button type="button" className="btn secondary" onClick={() => void setStatus('triaged')} disabled={working}>
                    Mark triaged
                  </button>
                )}
                <button type="button" className="btn secondary" onClick={() => void setStatus('in_progress')} disabled={working}>
                  Take in progress
                </button>
                <button type="button" className="btn secondary" onClick={() => void setStatus('resolved')} disabled={working}>
                  Resolve
                </button>
                <button type="button" className="btn secondary" onClick={() => void setStatus('closed')} disabled={working}>
                  Close
                </button>
              </div>
            </div>
          )}
          {detail.status === 'closed' && (
            <div style={{ marginTop: '12px' }}>
              <button type="button" className="btn secondary" onClick={() => void setStatus('in_progress')} disabled={working}>
                Reopen (in progress)
              </button>
            </div>
          )}
          {(detail.status === 'resolved' || detail.status === 'closed') && (
            <div style={{ marginTop: '12px' }}>
              {/* Tracker #107 slice C: the desk deflects its own future tickets. Prefill
                  only the subject — staff write and redact the answer themselves. */}
              <button
                type="button"
                className="btn secondary"
                disabled={working}
                onClick={() => {
                  setPromotedDraft({
                    ...emptyAnswerDraft(),
                    id: suggestAnswerId(detail.subject),
                    title: detail.subject,
                    family: detail.category ?? ''
                  });
                  setDetail(undefined);
                  setView('answers');
                }}
              >
                Promote to answer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SupportDesk;
